import clsx from "clsx";
import React, {
  ForwardRefExoticComponent,
  MutableRefObject,
  RefAttributes,
  useCallback,
  useEffect,
  useRef,
} from "react";

import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/outline";

import styles from "./index.module.css";
import useCarousel from "./use-carousel";

export const PrevButton = ({ className = styles.carouselButtonBlue }) => {
  const { goToPrevious, stepIndex, infinite } = useCarousel();
  return (
    <button onClick={goToPrevious} className={className} disabled={!infinite && stepIndex === 0} aria-label="Previous">
      <ChevronLeftIcon className={styles.carouselButtonIcon} />
    </button>
  );
};

export const NextButton = ({ className = styles.carouselButtonBlue }) => {
  const { goToNext, stepIndex, steps, infinite } = useCarousel();
  return (
    <button
      onClick={goToNext}
      className={className}
      disabled={!infinite && stepIndex === steps.length - 1}
      aria-label="Next"
    >
      <ChevronRightIcon className={styles.carouselButtonIcon} />
    </button>
  );
};

export type StepRendererProps = { step: any; className: string };
const transitionClasses = ["transition-transform", "duration-300"];

type Props = {
  StepRenderer: ForwardRefExoticComponent<StepRendererProps & RefAttributes<HTMLDivElement>>;
};

const Carousel = ({ StepRenderer }: Props) => {
  const { containerRef, stepIndex, startSwipe, moveSwipe, endSwipe, steps, activeStepRef, infinite } = useCarousel();
  const innerRef = useRef<HTMLDivElement>(null);
  const stepIndexRef = useRef(stepIndex);
  const timeoutRef = useRef<NodeJS.Timeout>(null) as MutableRefObject<NodeJS.Timeout>;
  const widthRef = useRef(0);

  const adjustHeight = useCallback(() => {
    if (!containerRef.current || !activeStepRef.current) {
      return;
    }

    widthRef.current = window.innerWidth;

    // Update the height to the active step content
    const stepHeight = activeStepRef.current.getBoundingClientRect()?.height;
    containerRef.current.style.height = `${stepHeight}px`;
  }, [containerRef, activeStepRef]);

  const handleResize = useCallback(() => {
    if (widthRef.current !== window.innerWidth) {
      widthRef.current = window.innerWidth;
      adjustHeight();
    }
  }, [adjustHeight]);

  useEffect(() => {
    // If going from first back to last, or if going from last to first, adjust transition
    const fromLastToFirst = stepIndex === 0 && stepIndexRef.current === steps.length - 1;
    const fromFirstToLast = stepIndex === steps.length - 1 && stepIndexRef.current === 0;

    if (innerRef.current) {
      if (infinite && (fromLastToFirst || fromFirstToLast)) {
        innerRef.current.classList.remove(...transitionClasses);

        if (fromFirstToLast) {
          innerRef.current.style.transform = `translateX(-${(stepIndex + 1) * 100}%)`;
        } else {
          innerRef.current.style.transform = "translateX(100%)";
        }

        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
          if (innerRef.current) {
            innerRef.current.classList.add(...transitionClasses);
            if (fromFirstToLast) {
              innerRef.current.style.transform = `translateX(-${stepIndex * 100}%)`;
            } else {
              innerRef.current.style.transform = "translateX(0%)";
            }
          }
        }, 10);
      } else {
        innerRef.current.style.transform = `translateX(-${stepIndex * 100}%)`;
      }
    }

    stepIndexRef.current = stepIndex;

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [stepIndex, infinite, steps.length]);

  useEffect(() => {
    adjustHeight();
  }, [stepIndex, adjustHeight]);

  useEffect(() => {
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [handleResize]);

  return (
    <div className="whitespace-nowrap overflow-hidden transition-all" ref={containerRef}>
      <div
        className={clsx(...transitionClasses)}
        onTouchStart={startSwipe}
        onTouchMove={moveSwipe}
        onTouchEnd={endSwipe}
        ref={innerRef}
      >
        {steps.map((step, index) => (
          <StepRenderer
            step={step}
            key={step?.title || index}
            className={clsx(
              "w-full align-top whitespace-normal transition-opacity",
              index === stepIndex ? "opacity-1" : "opacity-0"
            )}
            ref={index === stepIndex ? activeStepRef : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default Carousel;
